import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';

import * as EmployeeModels from '@employee-models/index';
import {ApiConfig} from '@employee-configs/api.config';
import {catchError, map} from 'rxjs/operators';
import {ICCureClearanceDto, IFunctionsDto, IServiceAreaDto, IUserLocationsResponse} from '@employee-models/data.models'; // ADD IUserLocationsResponse
import {CountryEnum} from '@shared-models/common.models';
import {BadgeRequestCategoryEnum} from '@employee-models/data.enums';

export interface IPeoplePickerLookupRequest {
    searchText: string;
}

@Injectable({
    providedIn: 'root'
})
export class UsersService {

    lookupPeopleFunction: (searchCriteria: string) => Observable<Partial<EmployeeModels.DM.IUserData>[]> = this.lookupPeople.bind(this);

    constructor(private http: HttpClient) {
    }


    searchApproverEmployee(searchCriteria: string, reqCategory: BadgeRequestCategoryEnum, params: {
        employeeFunctionId?: string;
        serviceArea?: string;
    }): Observable<any> {
        const paramsValue = { searchCriteria, ...params };
        let url = `${ApiConfig.UserManagement.searchApproverEmployee}`;

        switch (reqCategory) {
            case BadgeRequestCategoryEnum.MemberFirm:
            case BadgeRequestCategoryEnum.NonDeloitte:
                url = `${ApiConfig.UserManagement.searchApproverSM}`;
                break;
            case BadgeRequestCategoryEnum.ThirdPartyContractor:
                url = `${ApiConfig.UserManagement.searchApprover3rdParty}`;
                break;
        }
        return this.http.get<Observable<any>>(url, { params: paramsValue });
    }

    searchIRCApproverEmployee(searchCriteria: string, reqCategory: BadgeRequestCategoryEnum, params: {
        employeeFunctionId?: string;
        serviceArea?: string;
    }): Observable<any> {
        const paramsValue = { searchCriteria, ...params };
        const url = `${ApiConfig.UserManagement.searchApproverIRC}`;
        return this.http.get<Observable<any>>(url, { params: paramsValue });
    }

    // TODO switch to BE full-name
    searchEmployeeInLocation(searchCriteria: string): Observable<any> {
        return this.http.get<Observable<any>>(`${ApiConfig.UserManagement.searchEmployeeInLocation}`,
            { params: { searchCriteria } })
            .pipe(map(data => {
                data.forEach(item => {
                    item.fullName = `${item.firstName} ${item.lastName}`;
                });
                return data;
            }));
    }

    searchPPMDEmployee(searchCriteria: string): Observable<any> {
        return this.http.get<Observable<any>>(`${ApiConfig.UserManagement.searchPPMDEmployee}`,
            { params: { searchCriteria } })
            .pipe(map(data => {
                data.forEach(item => {
                    item.fullName = `${item.firstName} ${item.lastName}`;
                });
                return data;
            }));
    }

    searchPerson(searchCriteria: string, personType: 'employee' | 'contractor'): Observable<any> {
        const url = ApiConfig.UserManagement.searchOnBehalf;
        //  personType === 'employee' 
        //     ? `${ApiConfig.UserManagement.searchEmployee}` 
        //     : `${ApiConfig.UserManagement.searchContractor}`;
        const isContractor = personType === 'contractor' ? 'true' : 'false';
        return this.http.get<Observable<any>>(url, { params: { searchCriteria, isContractor } })
            .pipe(map(data => {
                data.forEach(item => {
                    item.fullName = `${item.firstName} ${item.lastName}`;
                });
                return data;
            }));
    }

    searchPPMDPrehire(searchCriteria: string): Observable<any> {
        return this.http.get<Observable<any>>(`${ApiConfig.UserManagement.searchPPMDPrehire}`,
            { params: { searchCriteria } })
            .pipe(map(data => {
                data.forEach(item => {
                    item.fullName = `${item.firstName} ${item.lastName}`;
                });
                return data;
            }));
    }


    lookupPeople(searchCriteria: string): Observable<Partial<EmployeeModels.DM.IUserData>[]> {
        return this.http.get(
            `${ApiConfig.UserManagement.searchEmployee}`, { params: { searchCriteria } }
        ) as Observable<Partial<EmployeeModels.DM.IUserData>[]>;
    }

    addEmployee(
        requestParams: EmployeeModels.CM.IEmployeeAddRequest
    ): Observable<{error?: HttpErrorResponse, response?: Record<string, string>}> {
        return this.http.post(ApiConfig.UserManagement.addEmployee, requestParams)
            .pipe(
                map(response => {
                    return ({ response });
                }),
                catchError(error => {
                    return of({ error });
                })
            ) as Observable<{error?: HttpErrorResponse, response?: Record<string, string>}>;
    }

    // tslint:disable-next-line:max-line-length
    getGridOfUser(gridRequest: EmployeeModels.DM.IUserGridRequest): Observable<EmployeeModels.DM.ITableWithFilterModelGeneric<EmployeeModels.DM.IEmployeeGridModel>> {
        // tslint:disable-next-line:max-line-length
        return this.http.post(ApiConfig.UserManagement.getGridOfUser, gridRequest) as Observable<EmployeeModels.DM.ITableWithFilterModelGeneric<EmployeeModels.DM.IEmployeeGridModel>>;
    }
    exportGridOfUser(gridRequest: EmployeeModels.DM.IUserGridRequest): Observable<EmployeeModels.DM.ITableWithFilterModelGeneric<EmployeeModels.DM.IEmployeeGridModel>> {
        // tslint:disable-next-line:max-line-length
        return this.http.post(ApiConfig.UserManagement.exportGridOfUser, gridRequest) as Observable<EmployeeModels.DM.ITableWithFilterModelGeneric<EmployeeModels.DM.IEmployeeGridModel>>;
    }

    getAvailableSites(country: CountryEnum | null = null): Observable<EmployeeModels.DM.ISiteDto[]> {
        const countryParam = (country)
            ? `?countryName=${country}`
            : '';
        return this.http.get(ApiConfig.Dictionary.getAvailableSites) as Observable<EmployeeModels.DM.ISiteDto[]>;
    }

    getAllServiceAreas(): Observable<IServiceAreaDto[]> {
        return this.http.get<IServiceAreaDto[]>(ApiConfig.Dictionary.getAllServiceAreas);
    }

    getUserServiceAreas(): Observable<IServiceAreaDto[]> {
        return this.http.get<IServiceAreaDto[]>(ApiConfig.Dictionary.getAvailableServiceArea);
    }

    getRoles(): Observable<EmployeeModels.DM.IEmployeeRoleModel[]> {
        return this.http.get(ApiConfig.Dictionary.getRoles) as Observable<EmployeeModels.DM.IEmployeeRoleModel[]>;
    }

    deleteUser(userId: number): Observable<null> {
        return this.http.post(`${ApiConfig.UserManagement.removeEmployee}/${userId}`, {}) as Observable<null>;
    }

    getSites(country: CountryEnum | null = null): Observable<EmployeeModels.DM.ISiteDto[]> {
        const countryParam = (country)
            ? `?countryName=${country}`
            : '';
        return this.http.get<EmployeeModels.DM.ISiteDto[]>(ApiConfig.Dictionary.getSites + countryParam);
    }

    getAllSites(country: CountryEnum | null = null): Observable<EmployeeModels.DM.ISiteDto[]> {
        const countryParam = (country)
            ? `?countryName=${country}`
            : '';
        return this.http.get<EmployeeModels.DM.ISiteDto[]>(ApiConfig.Dictionary.getAllSites + countryParam);
    }

    getAllSitesRange(gridRequest: EmployeeModels.DM.IUserGridRequest): Observable<IUserLocationsResponse> {
        return this.http.post(ApiConfig.Dictionary.getAllSitesRange, gridRequest) as Observable<IUserLocationsResponse>;
    }

    getAllSitesRangeExport(gridRequest: EmployeeModels.DM.IUserGridRequest): Observable<IUserLocationsResponse> {
        return this.http.post(ApiConfig.Dictionary.getAllSitesRangeExport, gridRequest) as Observable<IUserLocationsResponse>;
    }

    getVisitorSites(country: CountryEnum | null = null): Observable<EmployeeModels.DM.ISiteDto[]> {
        const countryParam = (country)
            ? `?countryName=${country}`
            : '';
        return this.http.get<EmployeeModels.DM.ISiteDto[]>(ApiConfig.Dictionary.getVisitorSites + countryParam);
    }

    getCountries(): Observable<EmployeeModels.DM.ICountryDto[]> {
        return this.http.get<EmployeeModels.DM.ICountryDto[]>(ApiConfig.Dictionary.getCountries);
    }

    getFunctions(): Observable<IFunctionsDto[]> {
        return this.http.get<IFunctionsDto[]>(ApiConfig.Dictionary.getFunctions);
    }

    getCCureClearance(): Observable<ICCureClearanceDto[]> {
        return this.http.get<ICCureClearanceDto[]>(ApiConfig.Dictionary.getCCureClearance);
    }
}